import { useState } from "react";
import { isAxiosError } from "axios";
import { apiClient } from "@/lib/api"; 
import { setUserSession } from "@/lib/session"; 
import Cookies from "js-cookie";

const getErrorMessage = (err: unknown, fallbackMessage: string): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message || err.message || fallbackMessage;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallbackMessage;
};

export interface UserAdditionalData {
  user_id: number;
  limit_sebar: number;
  limit_ai_convert: number; 
}

export interface UserProfileData {
  id: number;
  username: string;
  name: string;
  email: string;
  photo: string;
  wa_no: string;
  ig_link: string;
  email_verified_at: string | null;
  role_id: number;
  created_at: string;
  updated_at: string;
  additional: UserAdditionalData | null;
}

export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  confirm_password: string;
  redirect_url?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
  redirect_url?: string;
}

export interface LoginRegisterPayload {
  email: string;
  password: string;
  redirect_url?: string;
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
  confirm_password: string;
}

export interface UpdateProfilePayload {
  name: string;
  email: string;
}

export interface ChangePasswordPayload {
  password: string;
  confirm_password: string;
}

export const useAuthentication = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;

  const registerUser = async (payload: RegisterPayload) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        credentials: "include", 
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Gagal melakukan registrasi.");
      return { success: true, data };
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, "Terjadi kesalahan pada server saat registrasi.");
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const loginUser = async (payload: LoginPayload) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${baseUrl}/auth/login`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "Accept": "application/json" 
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Email atau password salah.");
      }

      // Ambil profile user setelah login berhasil
      try {
        const meResponse = await apiClient.get('/api/me');
        const userData: UserProfileData = meResponse.data?.data || meResponse.data;

        if (userData) {
          setUserSession(userData);
        }
      } catch (meError: unknown) {
        console.warn("Gagal Fetch Profil setelah Login:", meError);
      }

      return { success: true, data };

    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, "Terjadi kesalahan pada server.");
      setError(errorMessage);
      return { success: false, error: errorMessage };

    } finally {
      setIsLoading(false);
    }
  };


  const loginRegisterUser = async (payload: LoginRegisterPayload) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${baseUrl}/auth/login-register`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "Accept": "application/json" 
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Gagal memproses akun.");
      }

      // Cek apakah response memerlukan verifikasi email / pending OTP (status 201)
      const isPendingVerification =
        data?.code === 201 ||
        data?.message?.toLowerCase().includes("verification") ||
        data?.message?.toLowerCase().includes("check your email");

      // Hanya set auth_token dan fetch /api/me jika user sudah aktif terautentikasi (bukan pending verifikasi)
      if (!isPendingVerification) {
        const token = data?.token || data?.data?.token || data?.access_token || data?.data?.access_token;
        if (token) {
          Cookies.set("auth_token", token, { expires: 7 });
          if (typeof window !== "undefined") {
            localStorage.removeItem("token");
          }
        }

        try {
          const meResponse = await apiClient.get('/api/me');
          const userData: UserProfileData = meResponse.data?.data || meResponse.data;

          if (userData) {
            setUserSession(userData);
          }
        } catch (meError: unknown) {
          console.warn("Gagal Fetch Profil setelah Login/Register:", meError);
        }
      }

      return { success: true, data };

    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, "Terjadi kesalahan pada server saat login atau registrasi.");
      setError(errorMessage);
      return { success: false, error: errorMessage };

    } finally {
      setIsLoading(false);
    }
  };

  // Menerima parameter redirect_url opsional
  const loginWithGoogle = async (redirectUrl?: string) => {
    setError(null);
    try {
      const queryParam = redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : "";
      const response = await fetch(`${baseUrl}/auth/provider/google${queryParam}`, {
        method: "GET",
        headers: { "Accept": "application/json" },
      });
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.message || "Gagal mendapatkan URL Google Login.");
      if (!data.url) throw new Error("URL Google Login tidak ditemukan dari server.");
      
      return { success: true, url: data.url };
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, "Terjadi kesalahan saat memuat Google Login.");
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const forgotPassword = async (payload: ForgotPasswordPayload) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to send reset link.");
      return { success: true, data };
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, "A server error occurred.");
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (payload: ResetPasswordPayload) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl}/auth/reset-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Gagal mereset password. Link mungkin sudah tidak valid.");
      return { success: true, data };
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, "Terjadi kesalahan pada server.");
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (payload: UpdateProfilePayload) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.put('/api/profile', payload);
      const meResponse = await apiClient.get('/api/me');

      const userData: UserProfileData = meResponse.data?.data || meResponse.data;
      setUserSession(userData);
      
      return { success: true, data: response.data };
    } catch (err: unknown) {
      const errMsg = getErrorMessage(err, "Terjadi kesalahan pada server saat update profil.");
      setError(errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (payload: ChangePasswordPayload) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.put('/api/profile/change-password', payload);
      return { success: true, data: response.data };
    } catch (err: unknown) {
      const errMsg = getErrorMessage(err, "Terjadi kesalahan pada server saat ubah password.");
      setError(errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    registerUser,
    loginUser, 
    loginRegisterUser,
    loginWithGoogle, 
    forgotPassword,
    resetPassword, 
    updateProfile, 
    changePassword, 
    isLoading,
    error,
  };
};